import { endpoints } from "@app/api/apis"
import PageHeaders from "@app/sharedComponents/CommonHeader/CommonHeader"
import { useAppSelector, useAppDispatch } from "@app/store/hooks"
import { themeColorSet, ThemedPressable, ThemedText, ThemedView } from "@app/theme"
import { getEpochTime } from "@app/utils"
import { formatIndianCurrency } from "@app/utils/utility"
import { ROUTE } from "@voltmoney/micro-frontend"
import axios from "axios"
import dayjs from "dayjs"
import { useEffect, useState } from "react"
import {ActivityIndicator, Image, ScrollView} from 'react-native'
import { PledgeHistoryItemShimmer } from './PledgeHistoryItemShimmer'
import { showErrorSheet } from "@app/store/slices/errorSlice"
import Button from "@app/sharedComponents/Button/Button"
import { CaretRight } from "@app/sharedComponents/Icons/Icons"
import handleApiErr from "@app/helper/handleApiErr"

interface PledgeHistoryItem {
  createdOn: number;
  totalValueOfUnpledgedAssets?: number;
  additionalCreditLimit?: number;
  status: string;
}

interface PledgeHistoryProps {
  navigation: any; // Replace with proper navigation type
}

const PledgeHistory: React.FC<PledgeHistoryProps> = ({navigation}) =>{

    const successColor = '#E7F4ED'
    const failedColor = '#FFE0E1'
    const inProgressColor = '#FEF4E6'


    const userDetails = useAppSelector((state) => state.auth.userDetails);
    const dispatch = useAppDispatch();

    const [pledgeHistory,setPledgeHistory] = useState<PledgeHistoryItem[]>([])
    const [showLoader,setShowLoader] = useState(false)

    const getPledgeHistory = () => {
        const url = endpoints.pledgeHistory + userDetails?.linkedCredits[0]?.creditId
        setShowLoader(true);
        
        axios.get(url)
            .then((res) => {
                const enhanceLimitRequestDetailsList = res?.data?.enhanceLimitRequestDetailsList || [];
                const revocationRequestDetailsList = res?.data?.revocationRequestDetailsList || [];
                const concatenatedList = enhanceLimitRequestDetailsList.concat(revocationRequestDetailsList);
                
                const sortedList = concatenatedList.sort((a, b) => {
                    const timestampA = a.createdOn;
                    const timestampB = b.createdOn;
                    return timestampB - timestampA;
                });
                
                setPledgeHistory(sortedList);
            })
            .catch((err) => {
                console.log('pledgedMFList error:', err);
              if(handleApiErr(err)){
                dispatch(
                    showErrorSheet({
                        isErrorSheetVisible: true,
                        errorMessage:err?.data?.message,
                        closeOnBackdropPress: true,
                        errorType: err?.data?.voltErrorCode || err?.data?.statusCode,
                        errorHeading: err?.data?.header,
                    })
                );
            }
            })
            .finally(() => {
                setShowLoader(false);
            });
    }


    useEffect(()=> {
        setShowLoader(true)
        getPledgeHistory()
    },[])

    const renderEmptyState = () => {  
        return(
            <ThemedView justifyContent="center" alignItems="center" flex={1} >
                      <Image source={{uri: "https://volt-images-alpha.s3.ap-south-1.amazonaws.com/pledge_history_empty.png"}}
                       style={{width: 200, height: 140, marginBottom: 24}}/>
                <ThemedText textAlign="center" variant="b1m">{"Looks like you haven't made \n any requests yet!"}</ThemedText>
            </ThemedView>
        )
    }

    const renderPledgeHistory = () => {
        let prevDate = null;

        return(
            <ScrollView>
                {pledgeHistory.map((pledgeHistoryDetails, index) => {
                    const date = new Date(pledgeHistoryDetails.createdOn);
                    const formattedDate = date.toDateString();
                    const dateToShow = dayjs(pledgeHistoryDetails?.createdOn).format('DD MMM, YYYY');
                    const shouldShowTypography = formattedDate !== prevDate;
                    prevDate = formattedDate;
                    
                    const isRemovePledge = pledgeHistoryDetails?.totalValueOfUnpledgedAssets
                    const pledgeStatus = pledgeHistoryDetails?.status
                    const pledgedHistoryStatus = isRemovePledge && pledgeStatus === STATUS.SUCCESS ? "RTAs notified" : pledgeStatus === STATUS.SUCCESS ? "Completed" : pledgeStatus === STATUS.FAILED ? "Rejected" : "In progress"
                    type ThemeColorKeys = 'bg-fill-success' | 'bg-fill-error' | 'bg-fill-warning' | 'text-success' | 'text-error' | 'text-warning';
                    
                    let pledgedHistoryBackgroundColor: ThemeColorKeys;
                    let pledgedHistoryTextColor: ThemeColorKeys;
                    
                    if (pledgeStatus === STATUS.SUCCESS) {
                        pledgedHistoryBackgroundColor = 'bg-fill-success';
                        pledgedHistoryTextColor = 'text-success';
                    } else if (pledgeStatus === STATUS.FAILED) {
                        pledgedHistoryBackgroundColor = 'bg-fill-error';
                        pledgedHistoryTextColor = 'text-error';
                    } else {
                        pledgedHistoryBackgroundColor = 'bg-fill-warning';
                        pledgedHistoryTextColor = 'text-warning';
                    }


                    return (
                        <ThemedView mx="lg" my="sm">
                            {shouldShowTypography &&
                                <ThemedView my="sm">
                                  <ThemedText variant="b4s" color="text-secondary">
                                    {dateToShow}
                                  </ThemedText>
                                </ThemedView>
                            }
                            <ThemedView borderRadius="sm" borderWidth={1} padding="lg" borderColor="border-primary" >

                               <ThemedText variant="b3s" color="text-primary">{isRemovePledge ? "Remove pledge" : "Pledge more"}</ThemedText>
                               <ThemedView marginTop="sm" flexDirection="row" justifyContent="space-between">
                                <ThemedText variant="b4r" color="text-secondary">{isRemovePledge ? 'Portfolio value': 'Additional credit limit'}</ThemedText>
                                <ThemedText variant="b2s"  color="text-primary">{isRemovePledge? `₹${formatIndianCurrency(pledgeHistoryDetails?.totalValueOfUnpledgedAssets)}` : `₹${formatIndianCurrency(pledgeHistoryDetails?.additionalCreditLimit)}`}</ThemedText>
                               </ThemedView>
                               <ThemedView marginTop="sm"  flexDirection="row" justifyContent="space-between">
                               <ThemedText variant="b4r"  color="text-secondary">Status</ThemedText>
                                 <ThemedView backgroundColor={pledgedHistoryBackgroundColor} px="md" my="2xs" borderRadius="xs">
                                    <ThemedText color={pledgedHistoryTextColor} variant="b4r">{pledgedHistoryStatus.toUpperCase()}</ThemedText>
                                 </ThemedView>
                               </ThemedView>

                               <ThemedView my="lg" backgroundColor="border-secondary" height={1} width="100%" />
                               <ThemedView alignItems="flex-end">
                                <Button showRightAccessory={true} rightAccessory={() => <CaretRight size={12} color={themeColorSet['bg-action-primary']}/>}
                                 label="View details " type="TEXT" handlePress={()=> navigation.navigate(ROUTE.PLEDGE_DETAILS,  {assetList : pledgeHistoryDetails , isUnpledge : isRemovePledge})} />
                               </ThemedView>
                    
  
                            </ThemedView>
                        </ThemedView>
                    );
                })}
            </ScrollView>
        )
    }

    const renderShimmerList = () => {
        return (
            <ThemedView>
                <ScrollView contentContainerStyle={{paddingBottom: 40}}>
                    {[1, 2, 3, 4].map((_, index) => (
                        <PledgeHistoryItemShimmer key={index} />
                    ))}
                </ScrollView>
            </ThemedView>
        );
    };

    return(
        <>
        <ThemedView flex={1} backgroundColor="bg-fill-primary" paddingBottom="lg">
           <PageHeaders label="Pledge history" navigation={navigation} pageName={"PledgeHistory"}/>
              {showLoader ? renderShimmerList() : pledgeHistory.length === 0 ? renderEmptyState() : renderPledgeHistory()}
           

        </ThemedView>
        </>
    )



}

export default PledgeHistory

export enum STATUS  {
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
 }
 